/**
 * DemoEmailRoutingBanner — Shows a persistent banner when demo email routing
 * is active, including batch mode indicator.
 */
import { Mail, X, Layers } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useDemoEmailRouting } from "@/hooks/useDemoEmailRouting";
import { useState } from "react";

export default function DemoEmailRoutingBanner() {
  const { data: routing, isLoading } = useDemoEmailRouting();
  const [dismissed, setDismissed] = useState(false);

  if (isLoading || !routing?.enabled || dismissed) return null;

  const isBatchMode = routing.batch_mode !== false;

  return (
    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3 flex items-center gap-3">
      <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center shrink-0">
        <Mail className="w-4 h-4 text-amber-600 dark:text-amber-400" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-amber-900 dark:text-amber-200">Demo Email Routing Active</span>
          <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 dark:text-amber-400">
            Trial Mode
          </Badge>
          {isBatchMode && (
            <Badge variant="outline" className="text-[10px] border-blue-300 text-blue-700 dark:text-blue-400 gap-1">
              <Layers className="w-2.5 h-2.5" />
              Batch
            </Badge>
          )}
        </div>
        <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
          {isBatchMode
            ? "Emails are being grouped into a single test message per batch. No real members will be contacted."
            : `All emails are being routed to ${routing.redirect_email} for testing. No real members will be contacted.`}
        </p>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setDismissed(true)}
        className="shrink-0 h-7 w-7 p-0 text-amber-600 hover:text-amber-800"
      >
        <X className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}
